
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Mail, Loader2 } from "lucide-react";

interface SendOfferModalProps {
  offerUrl?: string;
  offerTitle?: string;
  onClose: () => void;
  userEmail?: string;
  isOpen?: boolean;
  onEmailGenerated?: (emailContent: string) => void;
}

const SendOfferModal: React.FC<SendOfferModalProps> = ({ 
  offerUrl = "", 
  offerTitle = "", 
  onClose,
  userEmail,
  isOpen = false,
  onEmailGenerated
}) => {
  const [recipientEmail, setRecipientEmail] = useState("");
  const [senderEmail, setSenderEmail] = useState(userEmail || "");
  const [subject, setSubject] = useState(`Ihr Versicherungsangebot: ${offerTitle}`);
  const [message, setMessage] = useState(`Guten Tag,\n\nanbei erhalten Sie Ihr Versicherungsangebot: ${offerTitle}.\n\nSie können das Angebot unter folgendem Link einsehen: ${offerUrl}\n\nBei Fragen stehe ich Ihnen gerne zur Verfügung.\n\nFreundliche Grüße`);
  const [isSending, setIsSending] = useState(false);
  const { toast } = useToast();

  // If the modal is not open, don't render anything
  if (!isOpen) {
    return null;
  }

  const handleSend = async () => {
    if (!recipientEmail) {
      toast({
        title: "Fehler",
        description: "Bitte geben Sie eine Empfänger-E-Mail-Adresse ein.",
        variant: "destructive"
      });
      return;
    }

    setIsSending(true);

    try {
      const { data, error } = await supabase.functions.invoke('send-email', {
        body: {
          from: senderEmail,
          to: recipientEmail,
          subject: subject,
          plainText: message,
          htmlContent: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #333;">
              <h2 style="color: #18B08D;">${subject}</h2>
              <div style="white-space: pre-wrap; margin: 20px 0;">
                ${message.replace(/\n/g, '<br>')}
              </div>
              <div style="margin: 30px 0; text-align: center;">
                <a href="${offerUrl}" style="background-color: #18B08D; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px; font-weight: bold;">
                  Angebot ansehen
                </a>
              </div>
            </div>
          `,
          offerDetails: {
            title: offerTitle,
            url: offerUrl
          }
        }
      });

      if (error) {
        throw error;
      }

      toast({
        title: "Erfolg",
        description: "Die E-Mail wurde erfolgreich versendet.",
      });
      
      if (onEmailGenerated) {
        onEmailGenerated(message);
      }
      
      onClose();
    } catch (error: any) {
      console.error("Error sending email:", error);
      toast({
        title: "Fehler",
        description: "Beim Senden der E-Mail ist ein Fehler aufgetreten.",
        variant: "destructive"
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">Angebot per E-Mail senden</h3>
      
      <div className="space-y-3">
        <div>
          <Label htmlFor="sender-email">Absender-E-Mail</Label>
          <Input
            id="sender-email"
            type="email"
            value={senderEmail}
            onChange={(e) => setSenderEmail(e.target.value)}
            className="mt-1"
          />
        </div>
        
        <div>
          <Label htmlFor="recipient-email">Empfänger-E-Mail</Label>
          <Input
            id="recipient-email"
            type="email"
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            className="mt-1"
          />
        </div>
        
        <div>
          <Label htmlFor="subject">Betreff</Label>
          <Input
            id="subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="mt-1"
          />
        </div>
        
        <div>
          <Label htmlFor="message">Nachricht</Label>
          <textarea
            id="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={6}
            className="w-full mt-1 p-2 border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      </div>
      
      <div className="flex gap-2 justify-end pt-2">
        <Button variant="outline" onClick={onClose} disabled={isSending}>
          Abbrechen
        </Button>
        <Button onClick={handleSend} disabled={isSending}>
          {isSending ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Senden...
            </>
          ) : (
            <>
              <Mail className="h-4 w-4 mr-2" />
              Senden
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default SendOfferModal;
